// ─────────────────────────────────────────────────────────────────────────
// Albania Curated Itineraries — single source of truth for the Albanian
// destinations "Curated Itineraries" section. Mirrors the Romania itinerary
// shape (src/data/romaniaHub.ts) so the shared CuratedItineraries component
// can render either country.
// ─────────────────────────────────────────────────────────────────────────
import type { Itinerary } from './romaniaHub';

export const ALBANIA_BOOKING_LINK = '/booking';

// Reuse the existing Albania editorial imagery.
const IMG = {
  riviera: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963107484_b7256427.png',
  heritage: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963125104_57440e36.jpg',
  alps: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963143323_5560b7cf.jpg',
  coast: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963265999_69e2ac79.png',
  lakes: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963355289_49d2518e.png',
  sunset: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776963374389_3a69e0ca.jpg',
};

export const albaniaItineraries: Itinerary[] = [
  {
    id: 'riviera-escape',
    title: '4-Day Albanian Riviera Escape',
    duration: '4 Days',
    image: IMG.riviera,
    imageAlt: 'Turquoise coves of the Albanian Riviera at Ksamil and Himarë along the Ionian Sea, Albania.',
    summary: 'A sun-soaked long weekend along the Ionian coast — hidden coves, boutique beach clubs and golden-hour sailing.',
    regions: ['Albanian Riviera', 'Saranda', 'Ksamil'],
    days: [
      { day: 'Day 1', title: 'Arrival & Saranda', detail: 'Private transfer to the coast, check-in to a seafront boutique hotel and an evening waterfront dinner.' },
      { day: 'Day 2', title: 'Ksamil Islands', detail: 'A private boat day among the Ksamil islets, secluded swim stops and a beach-club lunch.' },
      { day: 'Day 3', title: 'Himarë & Gjipe', detail: 'Drive the Riviera to hidden Gjipe Beach and the whitewashed village of Himarë.' },
      { day: 'Day 4', title: 'Blue Eye & Departure', detail: 'The Blue Eye spring and a leisurely lunch before your onward transfer.' },
    ],
  },
  {
    id: 'coast-heritage',
    title: '6-Day Coast & Heritage',
    duration: '6 Days',
    image: IMG.heritage,
    imageAlt: 'Ottoman stone architecture of Berat, the City of a Thousand Windows, UNESCO Albania.',
    summary: 'The Ionian coast paired with Albania’s UNESCO stone cities — Berat, Gjirokastër and ancient Butrint.',
    regions: ['Albanian Riviera', 'Berat', 'Gjirokastër', 'Butrint'],
    days: [
      { day: 'Day 1', title: 'Tirana', detail: 'Arrival in the capital and an evening in the vibrant Blloku district.' },
      { day: 'Day 2', title: 'Berat', detail: 'The City of a Thousand Windows, its castle quarter and a family winery lunch.' },
      { day: 'Day 3', title: 'Gjirokastër', detail: 'The Ottoman stone city, its citadel and artisan bazaar by private guide.' },
      { day: 'Day 4', title: 'Butrint & Saranda', detail: 'The 2,500-year archaeological park, then to the Riviera coast.' },
      { day: 'Day 5', title: 'Ksamil', detail: 'A private boat day among turquoise coves and island beaches.' },
      { day: 'Day 6', title: 'Departure', detail: 'Coastal brunch and transfer for your onward journey.' },
    ],
  },
  {
    id: 'alps-adventure',
    title: '7-Day Albanian Alps Adventure',
    duration: '7 Days',
    image: IMG.alps,
    imageAlt: 'Dramatic peaks of the Accursed Mountains with the Theth to Valbona hiking trail, northern Albania.',
    summary: 'The dramatic Accursed Mountains — the legendary Theth-to-Valbona trail, alpine villages and a Komani Lake ferry.',
    regions: ['Albanian Alps', 'Theth', 'Valbona', 'Shkodër'],
    days: [
      { day: 'Day 1', title: 'Tirana → Shkodër', detail: 'Arrival and transfer to lakeside Shkodër, gateway to the Alps.' },
      { day: 'Day 2', title: 'Komani Lake Ferry', detail: 'The spectacular fjord-like ferry to Fierza and on to Valbona valley.' },
      { day: 'Day 3', title: 'Valbona', detail: 'Guided valley walks, mountain guesthouse stay and stargazing.' },
      { day: 'Day 4', title: 'Valbona → Theth', detail: 'The legendary alpine pass trek with a private mountain guide.' },
      { day: 'Day 5', title: 'Theth', detail: 'The Blue Eye of Theth, the church and the Grunas waterfall.' },
      { day: 'Day 6', title: 'Shkodër', detail: 'Return via Rozafa Castle and the old town by bicycle.' },
      { day: 'Day 7', title: 'Departure', detail: 'Transfer to Tirana for your flight home.' },
    ],
  },
  {
    id: 'grand-albania',
    title: '10-Day Grand Albania',
    duration: '10 Days',
    image: IMG.coast,
    imageAlt: 'Dramatic Albanian Riviera coastline with cliffs meeting the turquoise Ionian Sea, Albania.',
    summary: 'A signature grand tour — the capital, UNESCO stone cities, the Riviera coast and the Accursed Mountains.',
    regions: ['Tirana', 'Berat', 'Albanian Riviera', 'Albanian Alps'],
    days: [
      { day: 'Day 1–2', title: 'Tirana', detail: 'The capital’s energy, museums and Dajti Mountain cable car.' },
      { day: 'Day 3–4', title: 'Berat & Gjirokastër', detail: 'The UNESCO Ottoman stone cities and winery country.' },
      { day: 'Day 5–6', title: 'The Riviera', detail: 'Saranda, Butrint, Ksamil islands and hidden coves.' },
      { day: 'Day 7–8', title: 'Alps & Komani Lake', detail: 'The fjord-like ferry, Valbona and Theth valleys.' },
      { day: 'Day 9–10', title: 'Shkodër & Return', detail: 'Lakeside old town before your onward transfer.' },
    ],
  },
  {
    id: 'ultimate-albania',
    title: '14-Day Ultimate Albania Experience',
    duration: '14 Days',
    image: IMG.sunset,
    imageAlt: 'Golden sunset over the Albanian coastline — the definitive luxury Albania journey with Zera.',
    summary: 'The definitive coast-to-mountains odyssey — capital, heritage cities, the full Riviera, ancient lakes and the Alps.',
    regions: ['Tirana', 'Berat', 'Gjirokastër', 'Albanian Riviera', 'Lakes & Nature', 'Albanian Alps'],
    days: [
      { day: 'Day 1–2', title: 'Tirana', detail: 'The vibrant capital and its surrounds.' },
      { day: 'Day 3–4', title: 'Heritage Cities', detail: 'Berat and Gjirokastër, the UNESCO stone cities.' },
      { day: 'Day 5–7', title: 'The Riviera', detail: 'Butrint, Saranda, Ksamil, Himarë and Gjipe.' },
      { day: 'Day 8–9', title: 'Lakes & Nature', detail: 'The Blue Eye spring, Lake Ohrid and Pogradec.' },
      { day: 'Day 10–12', title: 'The Alps', detail: 'Komani Lake ferry, Valbona and the Theth pass trek.' },
      { day: 'Day 13–14', title: 'Shkodër & Return', detail: 'Lakeside days and your onward departure.' },
    ],
  },
];
